import { Button } from '@merels/ui'
import { Settings, Trash2 } from 'lucide-react'
import { Publisher, Game } from './types'

interface ProjectManageViewProps {
  activeGameId: string | null
  selectedGame: Game | null
  destinationPublisherSlug: string
  setDestinationPublisherSlug: (slug: string) => void
  destinations: Publisher[]
  transferring: boolean
  onTransfer: (gameId: string) => void
  deleting: boolean
  onDelete: (gameId: string) => void
}

export function ProjectManageView({
  activeGameId,
  selectedGame,
  destinationPublisherSlug,
  setDestinationPublisherSlug,
  destinations,
  transferring,
  onTransfer,
  deleting,
  onDelete,
}: ProjectManageViewProps) {
  return (
    <div className="rounded-lg border border-[#1e212f] bg-[#0c0d13] p-5 space-y-4 font-sans text-xs">
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-100 flex items-center space-x-2">
          <Settings className="h-4 w-4 text-[#e11d48]" />
          <span>Transfer Namespace Authority</span>
        </h3>
        <p className="text-[10px] text-muted-foreground mt-1">
          Move the logical game definition and its complete publishing history to a different publisher organization.
        </p>
      </div>

      {!activeGameId ? (
        <div className="rounded border border-yellow-950/40 bg-yellow-950/10 p-3.5 text-yellow-500 font-mono text-[10px]">
          WARNING: This project has not been published yet and does not have an active catalog namespace registration (publisherSlug/gameSlug is set to TBD). You can only transfer projects that have been pushed at least once.
        </div>
      ) : !selectedGame ? (
        <div className="rounded border border-red-950/40 bg-red-950/10 p-3.5 text-red-400 font-mono text-[10px]">
          ERROR: Game metadata for &quot;{activeGameId}&quot; could not be resolved in the main catalog. Check if the publisher slug was renamed.
        </div>
      ) : (
        <div className="space-y-4 pt-2">
          <div className="max-w-md space-y-2">
            <label className="text-[10px] text-muted-foreground uppercase font-bold block">Destination Publisher</label>
            <select
              value={destinationPublisherSlug}
              onChange={(e) => setDestinationPublisherSlug(e.target.value)}
              className="w-full h-8 px-2.5 rounded bg-[#151722] border border-[#272b3f] text-gray-200 focus:outline-none focus:border-primary text-xs"
            >
              <option value="">-- Select Destination Publisher --</option>
              {destinations.map((pub) => (
                <option key={pub.publisherId} value={pub.slug}>
                  {pub.displayName} ({pub.slug})
                </option>
              ))}
            </select>
          </div>

          <Button
            disabled={!destinationPublisherSlug || transferring}
            onClick={() => onTransfer(selectedGame.gameId)}
            className="h-8 text-xs px-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded"
          >
            {transferring ? 'Transferring Project...' : 'Transfer Project'}
          </Button>

          <div className="border-t border-[#1e212f] pt-4 space-y-2">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-red-400">Remove from Catalog</h4>
            <p className="text-[10px] text-muted-foreground">
              Permanently removes this game&apos;s catalog versions and channels. Project credentials and telemetry are retained so it can be republished.
            </p>
            <Button
              disabled={deleting}
              onClick={() => onDelete(selectedGame.gameId)}
              className="h-8 text-xs px-4 bg-red-700 hover:bg-red-800 text-white font-bold rounded"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              {deleting ? 'Removing Game...' : 'Remove Game'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
